import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// avaliacoes_usuario_agencia.comentario (sql/74) e o embedding aninhado
// job_evaluations -> controle_projetos (pra filtrar por agencia_id) ainda não
// estão no types.ts gerado -> cast, mesmo padrão dos demais hooks de agência.
const avaliacoesInternasDb = supabase as unknown as {
  from: (table: string) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

interface AvaliacaoInternaBruta {
  id: string;
  notas: Record<string, unknown> | null;
  comentario: string | null;
  created_at: string;
  user_id: string;
}

export interface AgenciaAvaliacaoInternaLeve {
  membro: string;
  notas: Record<string, unknown> | null;
  comentario: string | null;
  occurred_at: string;
}

/**
 * Nomes dos membros (profiles) das avaliações, buscados à parte só pelos ids
 * realmente citados — mesmo padrão do buscarNomesFreelancers em
 * useAgenciaTimeline.ts.
 */
async function buscarNomesMembros(ids: string[]): Promise<Record<string, string>> {
  if (!ids.length) return {};
  const { data, error } = await supabase.from("profiles").select("id, full_name").in("id", ids);
  if (error) throw error;
  const nomePorId: Record<string, string> = {};
  for (const linha of data ?? []) {
    if (linha.full_name) nomePorId[linha.id] = linha.full_name;
  }
  return nomePorId;
}

/**
 * Avaliações internas por membro da equipe da agência
 * (avaliacoes_usuario_agencia) — aba "Avaliações" da página de detalhe.
 * Consulta direta via PostgREST, sem passar pela RPC get_agencia_timeline
 * (payload de ~2,2MB), usada só pela aba "Linha do tempo". Escopo pela
 * agência via embedding aninhado job_evaluations -> controle_projetos
 * (mesmas FKs usadas pela policy aua_select do sql/150, seção 0).
 */
export function useAgenciaAvaliacoesInternas(agenciaId?: string | null) {
  return useQuery({
    queryKey: ["agencia-avaliacoes-internas", agenciaId],
    enabled: !!agenciaId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AgenciaAvaliacaoInternaLeve[]> => {
      const { data, error } = await avaliacoesInternasDb
        .from("avaliacoes_usuario_agencia")
        .select(
          "id, notas, comentario, created_at, user_id, job_evaluations!inner(controle_projeto_id, controle_projetos!inner(agencia_id))",
        )
        .eq("job_evaluations.controle_projetos.agencia_id", agenciaId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const linhas = (data as AvaliacaoInternaBruta[]) ?? [];
      const idsMembros = Array.from(new Set(linhas.map((linha) => linha.user_id).filter(Boolean)));
      const nomePorMembro = await buscarNomesMembros(idsMembros);

      return linhas.map((linha) => ({
        membro: nomePorMembro[linha.user_id] ?? "Usuário removido",
        notas: linha.notas,
        comentario: linha.comentario,
        occurred_at: linha.created_at,
      }));
    },
  });
}
